import React, { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosConfig';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import DialogSelect from '../Input/DialogSelect';
import Button from '../Input/Button';
import CircularProgress from '@material-ui/core/CircularProgress';


export default function Journal({ user, isParent, theme }) {
    const [journalEntry, setJournalEntry] = useState('');
    const [journalStyle, setJournalStyle] = useState('');
    const [journalStyleCSS, setJournalStyleCSS] = useState({background: ''});
    const [characterCount, setCharacterCount] = useState(0);
    const [journalEntries, setJournalEntries] = useState([]);
    const [searchParams, setSearchParams] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const useStyles = makeStyles((theme) => ({
        journalBase: {
            minWidth: 275,
            minHeight: 500,
            backgroundColor: 'white',
        },
        input: {
            height: '100%',
            resize: 'none',
            overflow: 'scroll',
            backgroundColor: 'white',
        },
        title: {
            fontSize: 32,
            fontFamily: 'GoodUnicorn',
            color: theme.primaryColor,
            padding: "10px 20px !important",
            margin: "0px !important",
            marginTop: "10px !important",
            textAlign: 'center',
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '20px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
        },
        noEntriesMessage: {
            textAlign: 'center',
            padding: '20px',
            color: '#666',
            fontStyle: 'italic',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            margin: '20px auto',
            maxWidth: '600px',
        },
        readOnlyNotice: {
            backgroundColor: 'rgba(240, 240, 240, 0.95)',
            padding: '10px',
            borderRadius: '4px',
            marginBottom: '10px',
            textAlign: 'center',
            color: '#666',
        },
        journalEntryContainer: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '20px',
            borderRadius: '8px',
            marginBottom: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
        controlsContainer: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '10px',
            borderRadius: '0 0 8px 8px',
            marginTop: '0px',
        },
        searchContainer: {
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            padding: '15px',
            borderRadius: '8px',
            marginTop: '20px',
        },
        homeGrid: {
            marginTop: "10px",
            minHeight: "100vh",
            position: "relative",
        }
    }));
    
    const classes = useStyles();
    
    // Create dynamic background style
    const backgroundStyle = {
        position: "fixed",
        top: "160px",
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: -1,
        opacity: 0.8,
        backgroundSize: "cover",
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed",
        background: journalStyleCSS.background || `url(${theme.backgroundLogo}) no-repeat center center fixed`
    };
    
    // Get the selected child ID if the user is a parent
    const getTargetUserId = () => {
        if (isParent) {
            const selectedChildId = window.localStorage.getItem('SELECTED_CHILD_ID');
            return selectedChildId || user.id;
        }
        return user.id;
    };
    
    useEffect(() => {
        fetchJournalEntries();
        // Only fetch style preference for non-parent users
        if (!isParent) {
            fetchJournalStylePreference();
        }
    }, [searchParams, isParent]);
    
    useEffect(() => {
        updateJournalStyleCSS();
    }, [journalStyle]);
    
    const fetchJournalEntries = async () => {
        setIsLoading(true);
        setError(null);
        
        try {
            const targetUserId = getTargetUserId();
            
            // Log for debugging
            console.log('Fetching journal entries for:', {
                currentUser: user.id,
                targetUser: targetUserId,
                isParent: isParent,
                searchParams: searchParams
            });
            
            // Use the general endpoint that supports user_id parameter
            const result = await axiosInstance.get(`/journal_entries`, { 
                params: {
                    ...searchParams,
                    user_id: targetUserId // Add user_id to params for parent viewing child's journal
                }
            });
            
            setJournalEntries(result.data);
        } catch (error) {
            console.error('Error fetching journal entries:', error);
            setError('Failed to load journal entries. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };
    
    const fetchJournalStylePreference = async () => {
        try {
            const result = await axiosInstance.get(
                `/user/${user.id}/getJournalStylePreference`
            );
            if (result.data && result.data.length > 0) {
                setJournalStyle(result.data[0].journal_style_choice);
            }
        } catch (error) {
            console.error('Error fetching journal style preference:', error);
        }
    };
    
    const updateJournalStyleCSS = () => {
        if (['ocean', 'hearts', 'nature'].includes(journalStyle)) {
            setJournalStyleCSS({ background: `url('/images/journal/${journalStyle}.jpg')` });
        } else if (journalStyle) {
            setJournalStyleCSS({ background: `url('/images/journal/${journalStyle}.png')` });
        }
    };
    
    const handleJournalInput = (e) => {
        const newEntry = e.target.value.slice(0, 2000);
        setJournalEntry(newEntry);
        setCharacterCount(newEntry.length);
    };
    
    const submitJournalEntry = async () => {
        if (user && journalEntry.trim() && !isParent) {
            setIsLoading(true);
            try {
                await axiosInstance.post(
                    `/journal`, 
                    { content: journalEntry }
                );
                setJournalEntry('');
                setCharacterCount(0);
                fetchJournalEntries();
            } catch (error) {
                console.error('Error creating journal entry:', error);
                setError('Failed to save journal entry. Please try again.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const saveJournalStyle = async (newStyle) => {
        try {
            await axiosInstance.post(
                `/user/${user.id}/updateJournalStylePreference`, 
                { item: newStyle }
            );
            setJournalStyle(newStyle);
        } catch (error) {
            console.error("Failed to update journal style:", error);
            alert("Error: Failed to update journal style. Please try again.");
        }
    };

    const filterByMonth = (e, month) => {
        if (month.props.children === 'Clear') {
            setSearchParams({ ...searchParams, month: undefined });
        } else {
            setSearchParams({
                ...searchParams,
                month: months.indexOf(month.props.children) + 1,
            });
        }
    };

    const filterByYear = (e, year) => {
        if (year.props.children === 'Clear') {
            setSearchParams({ ...searchParams, year: undefined });
        } else {
            setSearchParams({ ...searchParams, year: year.props.children });
        }
    };

    const filterByDate = (e) => {
        if (e.target.value === 'Clear') {
            setSearchParams({ ...searchParams, date: undefined });
        } else {
            setSearchParams({ ...searchParams, date: e.target.value });
        }
    };
    
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    const journalBordersDTO = {
        heading: 'Change your journal border?',
        defaultLabel: 'options',
        items: {
            birds_2: { name: 'Birds II', value: 'birds_2' },
            boats: { name: 'Boats', value: 'boats' },
            butterflies: { name: 'Butterflies', value: 'butterflies' },
            bugs: { name: 'Bugs', value: 'bugs' },
            cars: { name: 'Cars', value: 'cars' },
            cats: { name: 'Cats', value: 'cats' },
            cats_2: { name: 'Cats II', value: 'cats_2' },
            color_bunnies: { name: 'Bunnies', value: 'color_bunnies' },
            dog: { name: 'Dogs', value: 'dog' },
            ducks: { name: 'Ducks', value: 'ducks' },
            fish: { name: 'Fish', value: 'fish' },
            flowers: { name: 'Flowers', value: 'flowers' },
            flowers_2: { name: 'Flowers II', value: 'flowers_2' },
            frogs: { name: 'Frogs', value: 'frogs' },
            grasshopper: { name: 'Grasshoppers', value: 'grasshopper' },
            horses: { name: 'Horses', value: 'horses' },
            lucy: { name: 'Lucy', value: 'lucy' },
            swans: { name: 'Swans', value: 'swans' },
            three_birds: { name: 'Birds III', value: 'three_birds' }
        }
    };
    
    // Get selected child's name for display
    const getSelectedChildName = () => {
        if (isParent) {
            const selectedChildSettings = JSON.parse(
                window.localStorage.getItem('SELECTED_CHILD_SETTINGS') || '{}'
            );
            return selectedChildSettings.name || 'your child';
        }
        return null;
    };

    return (
        <div className={classes.homeGrid}>
            <div style={backgroundStyle}></div>
            <Grid
                container
                spacing={2}
                id={"HOC"}
            >
                    <Grid item xs={12} className={classes.title} style={{ position: 'relative' }}>
                        <h4 style={{ color: theme.primaryColor, margin: "0px"}}>
                            Journal {isParent && `- ${getSelectedChildName()}'s Entries`}
                        </h4>
                        <h5 style={{ color: theme.primaryColor, margin: "0px" }}>
                            {new Date().toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                            })}
                            {' '}
                            - Today
                        </h5>
                        {user && !isParent && (
                            <div style={{ position: 'absolute', top: '5px', right: '20px' }}>
                                <DialogSelect
                                    DTO={journalBordersDTO}
                                    saveJournalStyle={saveJournalStyle}
                                    user={user}
                                    theme={theme}
                                />
                            </div>
                        )}
                    </Grid>
                    
                    {/* Only show journal input container for child users */}
                    {!isParent && (
                        <>
                            <Grid item xs={3} />
                            <Grid
                                item
                                xs={6}
                            >
                                <Card className={classes.journalBase}>
                                    <TextField
                                        multiline
                                        fullWidth
                                        className={classes.input}
                                        inputProps={{
                                            style: {
                                                height: '500px',
                                                padding: '0 14px',
                                                overflow: 'scroll',
                                            }
                                        }}
                                        placeholder="What happened today?"
                                        variant="outlined"
                                        value={journalEntry}
                                        onChange={handleJournalInput}
                                        disabled={isLoading}
                                    />
                                </Card>
                            </Grid>
                            <Grid item xs={3} />
                            <Grid item xs={3} />
                            <Grid item xs={6} className={classes.controlsContainer}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography style={{ paddingLeft: '5px' }}>
                                        {characterCount}/2000
                                    </Typography>
                                    <Button
                                        theme={theme}
                                        style={{ fontWeight: 'bold' }}
                                        onClick={submitJournalEntry}
                                        disabled={isLoading || !journalEntry.trim()}
                                    >
                                        Done
                                    </Button>
                                </div>
                            </Grid>
                            <Grid item xs={3} />
                        </>
                    )}
                    
                    <Grid item xs={12} style={{ display: 'flex', justifyContent: 'center' }}>
                        <div className={classes.searchContainer} style={{ textAlign: 'center', maxWidth: '600px' }}>
                            <Typography variant="body1" style={{ marginBottom: '10px', fontWeight: 'bold' }}>
                                Search By
                            </Typography>
                            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '10px', justifyContent: 'center' }}>
                                <Select
                                    style={{ minWidth: '120px' }}
                                    displayEmpty
                                    value={searchParams.month || ''}
                                    onChange={filterByMonth}
                                    renderValue={(value) => months[value - 1] || 'Month'}
                                >
                                    {[
                                        'January',
                                        'February',
                                        'March',
                                        'April',
                                        'May',
                                        'June',
                                        'July',
                                        'August',
                                        'September',
                                        'October',
                                        'November',
                                        'December',
                                        'Clear'
                                    ].map((month) => (
                                        <MenuItem key={month}>{month}</MenuItem>
                                    ))}
                                </Select>
                                <span>or</span>
                                <TextField
                                    id="date"
                                    placeholder="Date"
                                    type="date"
                                    onChange={filterByDate}
                                    value={searchParams.date || ''}
                                    className={classes.textField}
                                    inputProps={{ placeholder: 'Date' }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                />
                                <span>or</span>
                                <Select
                                    style={{ minWidth: '100px' }}
                                    displayEmpty
                                    value={searchParams.year || ''}
                                    onChange={filterByYear}
                                    renderValue={(value) => value || 'Year'}
                                >
                                    {[2025, 2026, 'Clear'].map((year) => (
                                        <MenuItem key={year}>{`${year}`}</MenuItem>
                                    ))}
                                </Select>
                            </div>
                        </div>
                    </Grid>
            </Grid>
            
            <Grid container style={{ marginTop: '50px', paddingBottom: '50px' }}>
                {error && (
                    <Grid item xs={12}>
                        <Typography color="error" align="center">
                            {error}
                        </Typography>
                    </Grid>
                )}
                
                {isLoading ? (
                    <Grid item xs={12} className={classes.loadingContainer}>
                        <CircularProgress />
                    </Grid>
                ) : journalEntries.length === 0 ? (
                    <Grid item xs={12} className={classes.noEntriesMessage}>
                        <Typography variant="body1">
                            {isParent 
                                ? `${getSelectedChildName()} hasn't created any journal entries yet.`
                                : "You haven't created any journal entries yet."
                            }
                        </Typography>
                    </Grid>
                ) : (
                    journalEntries.map((journalEntry) => (
                        <React.Fragment key={journalEntry.id}>
                            <Grid item xs={3} />
                            <Grid item xs={6}>
                                <div className={classes.journalEntryContainer}>
                                    <h2>
                                        {new Date(journalEntry.created_at).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </h2>
                                    <Typography
                                        style={{ 
                                            overflowWrap: 'break-word', 
                                            overflow: 'auto',
                                            whiteSpace: 'pre-wrap',
                                            backgroundColor: isParent ? '#f9f9f9' : 'transparent',
                                            padding: isParent ? '10px' : '0',
                                            borderRadius: isParent ? '4px' : '0',
                                        }}
                                    >
                                        {journalEntry.content}
                                    </Typography>
                                </div>
                            </Grid>
                            <Grid item xs={3} />
                        </React.Fragment>
                    ))
                )}
            </Grid>
        </div>
    );
}